import { openSqlite } from "@skaile/workspaces/core/logging";
import type { LogEntry, LogQuery } from "@skaile/workspaces/types";
import { rowToEntry, type SqliteLogRow } from "./sqlite-row.ts";

export interface LocalTailOptions {
  dbPath: string;
  query: Omit<LogQuery, "sessionId"> & { sessionId: string };
  /** Poll interval in milliseconds (default 500). */
  intervalMs?: number;
  /** Called once per new entry, in chronological order. */
  onEntry: (entry: LogEntry) => void;
  /** Aborts the loop cleanly. */
  signal?: AbortSignal;
  /**
   * Initial cursor: only entries with `id > startAfterId` are streamed.
   * Used to skip rows already printed by the history snapshot before the
   * tail loop took over.
   */
  startAfterId?: string;
}

const DEFAULT_INTERVAL_MS = 500;
const TAIL_BATCH_LIMIT = 200;

/**
 * Live-poll a SQLite log DB and stream new entries to `onEntry`. Re-opens the
 * db read-only on every iteration (cheap; survives WAL rotation, hibernation,
 * and stale connections). Returns when `signal` is aborted.
 */
export async function localTail(opts: LocalTailOptions): Promise<void> {
  const intervalMs = opts.intervalMs ?? DEFAULT_INTERVAL_MS;
  let lastSeen = opts.startAfterId ?? "";

  while (!opts.signal?.aborted) {
    const db = openSqlite(opts.dbPath, { readonly: true });
    try {
      db.exec("PRAGMA query_only=1;");
      const { sql, params } = buildTailSelect({ ...opts.query, lastSeen });
      const rows = db.prepare(sql).all<SqliteLogRow>(params);
      for (const row of rows) {
        const entry = rowToEntry(row);
        opts.onEntry(entry);
        if (entry.id > lastSeen) lastSeen = entry.id;
      }
    } finally {
      db.close();
    }

    if (opts.signal?.aborted) return;
    await sleepUnlessAborted(intervalMs, opts.signal);
  }
}

interface BuiltSelect {
  sql: string;
  params: Record<string, unknown>;
}

/**
 * Tail-specific SELECT: `id > $lastSeen` instead of `id < $cursor`,
 * `ORDER BY id ASC` (chronological) instead of DESC, and a fixed
 * `LIMIT 200` per batch to bound a single iteration. Mirrors the rest of
 * the filter logic in `local-query`'s `buildSelect`.
 */
function buildTailSelect(
  q: Omit<LogQuery, "sessionId" | "cursor" | "limit"> & {
    sessionId: string;
    lastSeen: string;
  },
): BuiltSelect {
  const where: string[] = ["session_id = $sid", "id > $lastSeen"];
  const params: Record<string, unknown> = {
    $sid: q.sessionId,
    $lastSeen: q.lastSeen,
  };

  if (q.source?.kind) {
    where.push("kind = $kind");
    params.$kind = q.source.kind;
  }
  if (q.source?.subkind) {
    where.push("subkind = $sub");
    params.$sub = q.source.subkind;
  }
  if (q.source?.instance) {
    where.push("instance = $inst");
    params.$inst = q.source.instance;
  }
  if (q.levels && q.levels.length > 0) {
    const placeholders = q.levels.map((_, i) => `$lvl${i}`).join(", ");
    where.push(`level IN (${placeholders})`);
    q.levels.forEach((lvl, i) => {
      params[`$lvl${i}`] = lvl;
    });
  }
  if (q.since) {
    where.push("timestamp >= $since");
    params.$since = q.since;
  }
  if (q.until) {
    where.push("timestamp <= $until");
    params.$until = q.until;
  }
  if (q.search) {
    where.push("message LIKE $search");
    params.$search = `%${q.search}%`;
  }

  params.$limit = TAIL_BATCH_LIMIT;

  const sql = `
    SELECT id, timestamp, session_id, kind, subkind, instance, level, message, data_json, error_json
    FROM log_entries
    WHERE ${where.join(" AND ")}
    ORDER BY id ASC
    LIMIT $limit
  `;
  return { sql, params };
}

function sleepUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      resolve();
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}
